// src/components/services/taxpayerService.js
import api from "../components/Api/Api"; // Import the API instance

class TaxpayerService {

  /**
   * Registrar un nuevo contribuyente
   * @param {Object|FormData} taxpayerData - Datos del contribuyente
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async register(taxpayerData) {


    try {
      const config = {};

      if (taxpayerData instanceof FormData) {
        // Extraer XSRF token de las cookies
        const xsrfCookie = document.cookie
          .split('; ')
          .find(row => row.startsWith('XSRF-TOKEN='));

        const xsrfToken = xsrfCookie ?
          decodeURIComponent(xsrfCookie.split('=')[1]) :
          null;

        config.headers = {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': undefined // Explicitly remove Content-Type for FormData
        };

        // Agregar el token XSRF si existe
        if (xsrfToken) {
          config.headers['X-XSRF-TOKEN'] = xsrfToken;
        }
      }

      const response = await api.post("/api/register", taxpayerData, config);
      return {
        success: true,
        data: response.data,
        message: response.data.message || "Contribuyente registrado exitosamente"
      };
    } catch (error) {
      console.error("Error registering taxpayer:", error);

      if (error.response) {
        // Solo re-lanzar la respuesta del error
        throw error.response.data;
      } else if (error.request) {
        throw {
          message: "Error de conexión. Verifique su conexión a internet."
        };
      } else {
        throw {
          message: "Error inesperado. Intente nuevamente."
        };
      }
    }
  }

  /**
   * Obtener lista de contribuyentes
   * @param {Object} params - Parámetros de consulta (page, search, etc.)
   * @returns {Promise<Object>} Lista de contribuyentes
   */
  async getAll(params = {}) {
    try {
      const response = await api.get("/api/taxpayers", { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Error fetching taxpayers:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Obtener un contribuyente por ID
   * @param {number} id - ID del contribuyente
   * @returns {Promise<Object>} Datos del contribuyente
   */
  async getById(id) {
    try {
      const response = await api.get(`/api/taxpayers/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Error fetching taxpayer:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Actualizar un contribuyente
   * @param {number} id - ID del contribuyente
   * @param {Object|FormData} taxpayerData - Datos actualizados
   * @returns {Promise<Object>} Contribuyente actualizado
   */
  async update(id, taxpayerData) {
    try {
      const config = {};

      // Para FormData, NO establecer Content-Type manualmente
      if (taxpayerData instanceof FormData) {
        config.headers = {
          'Content-Type': undefined // Explicitly remove Content-Type for FormData
        };
      }

      const response = await api.put(`/api/taxpayers/${id}`, taxpayerData, config);
      return {
        success: true,
        data: response.data,
        message: "Contribuyente actualizado exitosamente"
      };
    } catch (error) {
      console.error("Error updating taxpayer:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Eliminar un contribuyente
   * @param {number} id - ID del contribuyente
   * @returns {Promise<Object>} Confirmación de eliminación
   */
  async deleteTaxpayer(id) {
    try {
      const response = await api.delete(`/api/taxpayers/${id}`);
      return {
        success: true,
        message: "Contribuyente eliminado exitosamente"
      };
    } catch (error) {
      console.error("Error deleting taxpayer:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Validar identificación
   * @param {string} identification - Número de identificación
   * @param {string} type - Tipo de identificación
   * @returns {Promise<Object>} Resultado de validación
   */
  async validateIdentification(identification, type = '01') {
    try {
      const response = await api.post("/api/validate-identification", {
        identification,
        identification_type: type
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Error validating identification:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Cambiar estado de un contribuyente
   * @param {number} id - ID del contribuyente
   * @param {string} status - Nuevo estado (active, inactive, suspended)
   * @returns {Promise<Object>} Contribuyente con estado actualizado
   */
  async changeStatus(id, status) {
    try {
      const response = await api.patch(`/api/taxpayers/${id}/status`, { status });
      return {
        success: true,
        data: response.data,
        message: `Estado cambiado a ${status} exitosamente`
      };
    } catch (error) {
      console.error("Error changing taxpayer status:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Actualizar estado activo/inactivo de un contribuyente
   * @param {number} id - ID del contribuyente
   * @param {boolean} is_active - Estado activo (true/false)
   * @returns {Promise<Object>} Contribuyente con estado actualizado
   */
  async updateTaxpayerStatus(id, is_active) {
    try {
      const response = await api.patch(`/api/taxpayers/${id}/status`, { is_active });
      return {
        success: true,
        data: response.data,
        message: `Contribuyente ${is_active ? 'activado' : 'desactivado'} exitosamente`
      };
    } catch (error) {
      console.error("Error updating taxpayer status:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Buscar contribuyentes
   * @param {string} query - Término de búsqueda
   * @param {Object} filters - Filtros adicionales
   * @returns {Promise<Object>} Resultados de búsqueda
   */
  async search(query, filters = {}) {
    try {
      const params = {
        search: query,
        ...filters
      };

      const response = await api.get("/api/taxpayers/search", { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Error searching taxpayers:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Exportar contribuyentes
   * @param {Object} filters - Filtros para exportación
   * @param {string} format - Formato de exportación (excel, pdf, csv)
   * @returns {Promise<Blob>} Archivo para descarga
   */
  async export(filters = {}, format = 'excel') {
    try {
      const response = await api.get("/api/taxpayers/export", {
        params: { ...filters, format },
        responseType: 'blob'
      });

      return {
        success: true,
        data: response.data,
        filename: this.getExportFilename(format)
      };
    } catch (error) {
      console.error("Error exporting taxpayers:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Manejo centralizado de errores
   * @param {Error} error - Error capturado
   * @returns {Object} Error formateado
   */
  handleError(error) {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 422:
          return {
            type: 'validation',
            errors: data.errors || {},
            message: data.message || "Error de validación"
          };
        case 404:
          return {
            type: 'not_found',
            message: "Contribuyente no encontrado"
          };
        case 403:
          return {
            type: 'forbidden',
            message: "No tiene permisos para realizar esta acción"
          };
        case 401:
          return {
            type: 'unauthorized',
            message: "Sesión expirada. Inicie sesión nuevamente"
          };
        case 500:
          return {
            type: 'server',
            message: "Error interno del servidor"
          };
        default:
          return {
            type: 'api',
            message: data.message || "Error en el servidor"
          };
      }
    } else if (error.request) {
      return {
        type: 'network',
        message: "Error de conexión. Verifique su conexión a internet."
      };
    } else {
      return {
        type: 'unknown',
        message: "Error inesperado. Intente nuevamente."
      };
    }
  }

  /**
   * Generar nombre de archivo para exportación
   * @param {string} format - Formato del archivo
   * @returns {string} Nombre del archivo
   */
  getExportFilename(format) {
    const date = new Date().toISOString().split('T')[0];
    const extensions = {
      excel: 'xlsx',
      pdf: 'pdf',
      csv: 'csv'
    };

    return `contribuyentes_${date}.${extensions[format] || 'xlsx'}`;
  }

  /**
   * Validar datos antes del envío
   * @param {Object} data - Datos a validar
   * @returns {Object} Resultado de validación
   */
  validateData(data) {
    const errors = {};
    const requiredFields = [
      'identification',
      'firstname',
      'lastname1',
      'email',
      'cellphone',
      'comercial_name',
      'province_id',
      'canton_id',
      'district_id',
      'neighborhood',
      'address',
      'sector_id',
      'regime_id'
    ];

    // Validar campos requeridos
    requiredFields.forEach(field => {
      if (!data[field] || (typeof data[field] === 'string' && data[field].trim() === '')) {
        errors[field] = `El campo ${field} es requerido`;
      }
    });

    // Validar email
    if (data.email && !/\S+@\S+\.\S+/.test(data.email)) {
      errors.email = "El formato del email no es válido";
    }

    // Validar teléfono (ejemplo para Costa Rica)
    if (data.cellphone && data.phone_country_code === '506') {
      if (!/^[0-9]{8}$/.test(data.cellphone.replace(/[-\s]/g, ''))) {
        errors.cellphone = "El teléfono debe tener 8 dígitos";
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

export default new TaxpayerService();